/**
 * AppShell — layout global da aplicação autenticada.
 *
 * Spec §10 (Layout Global):
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ HEADER                                                        │
 *   ├─────────────┬────────────────────────────────────────────────┤
 *   │             │                                                 │
 *   │  SIDEBAR    │  <Outlet />  (PAGE CONTENT)                    │
 *   │             │                                                 │
 *   └─────────────┴────────────────────────────────────────────────┘
 *
 * Responsabilidades:
 *  • Estruturar grid Header (topo, fixo) + Sidebar (esquerda, fixa) + Outlet.
 *  • Instanciar `useNotificationStream()` — **apenas** quando
 *    `auth.mode === 'jwt'`. No modo basic o admin global não tem JWT e não
 *    recebe SSE.
 *
 * Implementação do hook condicional:
 *  React proíbe chamar hooks condicionalmente NUMA MESMA função, mas é
 *  perfeitamente válido renderizar um componente filho que SEMPRE chama o
 *  hook na sua própria ordem estável. Aqui usamos `<NotificationStreamHost />`
 *  como filho montado apenas em `mode === 'jwt'`. Quando o modo muda, o filho
 *  desmonta — o cleanup do useEffect dentro do hook fecha a conexão SSE.
 */
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useNotificationStream } from '@/hooks/useNotificationStream'
import { useAuthStore } from '@/store/auth.store'
import { useSidebarStore } from '@/store/sidebar.store'
import { isAutoCollapseRoute } from '@/lib/sidebarRoutes'

import { Header } from './Header'
import { Sidebar } from './Sidebar'

/**
 * Componente "host" do SSE global. Sempre chama `useNotificationStream` na
 * mesma ordem — montado somente quando o modo é JWT.
 */
function NotificationStreamHost(): null {
  useNotificationStream()
  return null
}

export function AppShell(): React.ReactElement {
  const mode = useAuthStore((s) => s.mode)
  const isJwt = mode === 'jwt'

  // Auto-colapso da Sidebar em rotas densas (canvas, device, execução, preview).
  // Roda a cada troca de rota; o usuário ainda pode expandir manualmente.
  const { pathname } = useLocation()
  const applyRoute = useSidebarStore((s) => s.applyRoute)
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => window.innerWidth < 768,
  )

  useEffect(() => {
    const handleResize = (): void => setIsNarrowViewport(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    applyRoute(isNarrowViewport || isAutoCollapseRoute(pathname))
  }, [pathname, applyRoute, isNarrowViewport])

  return (
    <div
      data-testid="app-shell"
      className="flex h-screen w-screen flex-col overflow-hidden bg-background text-foreground"
    >
      {/* SSE global — só montado em modo JWT. Em modo basic NÃO instancia. */}
      {isJwt ? <NotificationStreamHost /> : null}

      <Header />

      <div className="flex min-h-0 flex-1">
        <Sidebar />

        <main
          data-testid="app-content"
          className="flex-1 overflow-y-auto"
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
